import { Component, ElementRef, ViewChild } from '@angular/core';
import { UserInfoEngine } from '@memberjunction/core-entities';
import { OEPViewBase } from '../state/oep-view-base';
import { OEPCaptureOpener, OEPRestoreFocus, OEPTrapTab } from './dialog-focus';
import { IsLastTourStep, OEP_TOUR, OEP_TOUR_DONE, OEP_TOUR_SETTING, ShouldShowTour, TourNextLabel, TourStepLabel, type OEPTourStep } from './oep-tour.vm';

/**
 * The one-time first-login tour (markup.html:951-967, logic.js:139-143) in mj-slide-panel
 * Mode="dialog". Shown once per user: the 'oep.tour.v1' UserInfoEngine setting (a MJ: User Settings
 * row, cached by the engine) is read on init and written, debounced, on Get started or Skip. Escape
 * and a backdrop click both reach mj-slide-panel's Closed output and count as Skip. The panel adds no
 * focus handling, so this moves focus onto the primary button, traps Tab, and returns focus on close.
 */
@Component({
    standalone: false,
    selector: 'oep-tour',
    templateUrl: './oep-tour.component.html',
    styleUrl: './oep-tour.component.scss',
})
export class OEPTourComponent extends OEPViewBase {
    readonly Steps: readonly OEPTourStep[] = OEP_TOUR;
    Open = false;
    Step = 0;
    private opener: HTMLElement | null = null;

    /** Moves focus into the dialog when the button first renders (the panel mounts with @if Open). */
    @ViewChild('NextButton') set NextButton(el: ElementRef<HTMLButtonElement> | undefined) {
        el?.nativeElement.focus();
    }

    protected async Load(): Promise<void> {
        if (this.Open) return;
        try {
            await UserInfoEngine.Instance.Config(false);
            if (!ShouldShowTour(UserInfoEngine.Instance.GetSetting(OEP_TOUR_SETTING))) return;
        } catch {
            return; // Settings unavailable: no tour this session rather than a blocked page.
        }
        this.opener = OEPCaptureOpener(document);
        this.Step = 0;
        this.Open = true;
    }

    get Current(): OEPTourStep { return this.Steps[this.Step]; }
    get StepLabel(): string { return TourStepLabel(this.Step); }
    get NextLabel(): string { return TourNextLabel(this.Step); }

    Next(): void {
        if (IsLastTourStep(this.Step)) this.Finish();
        else this.Step += 1;
        this.Changed();
    }

    /** Get started, Skip, Escape and backdrop click all end here; idempotent. */
    Finish(): void {
        if (!this.Open) return;
        this.Open = false;
        UserInfoEngine.Instance.SetSettingDebounced(OEP_TOUR_SETTING, OEP_TOUR_DONE);
        OEPRestoreFocus(this.opener);
        this.opener = null;
        this.Changed();
    }

    TrapTab(event: KeyboardEvent): void {
        OEPTrapTab(event, event.currentTarget as HTMLElement);
    }
}
